import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  CheckCircle2, 
  Loader2,
  Clock,
  XCircle,
  AlertTriangle,
  CreditCard,
  Gift,
  TestTube2,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Subscription = () => {
  const { subscription, refreshUserData, isTrialActive, getTrialDaysLeft } = useAuth();
  const [cancelling, setCancelling] = useState(false);
  const [loading, setLoading] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [trialEligibility, setTrialEligibility] = useState(null);
  const [testModeInfo, setTestModeInfo] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // monthly or annual

  useEffect(() => {
    fetchAlerts();
    checkTrialEligibility();
    checkTestModeInfo();
  }, []);

  const checkTestModeInfo = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/test-mode-info`);
      setTestModeInfo(response.data);
    } catch (error) {
      console.error("Error checking test mode info:", error);
    }
  };

  const checkTrialEligibility = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/trial-eligibility`);
      setTrialEligibility(response.data);
    } catch (error) {
      console.error("Error checking trial eligibility:", error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/alerts`);
      setAlerts(response.data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      await axios.put(`${API}/subscriptions/alerts/${alertId}/read`);
      setAlerts(alerts.filter(a => a.id !== alertId));
    } catch (error) {
      console.error("Error dismissing alert:", error);
    }
  };

  const handleUpdatePayment = async () => {
    setLoading("update-payment");
    try {
      const response = await axios.post(`${API}/subscriptions/update-payment`, {
        origin_url: window.location.origin
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error updating payment:", error);
      toast.error("Erreur lors de la mise à jour du paiement");
    } finally {
      setLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler votre abonnement ?\n\n✓ Les prélèvements Stripe seront immédiatement arrêtés\n✓ Vous ne serez plus facturé\n✗ Vous perdrez l'accès aux fonctionnalités premium\n✗ Votre QR Code et lien de collecte seront désactivés")) {
      return;
    }
    
    setCancelling(true);
    
    try {
      const response = await axios.post(`${API}/subscriptions/cancel`);
      await refreshUserData();
      toast.success("Abonnement annulé - Plus aucun prélèvement ne sera effectué");
    } catch (error) {
      console.error("Error cancelling:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setCancelling(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading("subscribe");
    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        plan: "starter",
        billing_period: billingPeriod,
        origin_url: window.location.origin
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      } else if (response.data.success) {
        // Mock payment for testing
        await refreshUserData();
        toast.success("Abonnement activé !");
      }
    } catch (error) {
      console.error("Error starting subscription:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'abonnement");
    } finally {
      setLoading(null);
    }
  };

  // Build features list dynamically based on trial eligibility
  const getFeatures = () => {
    const baseFeatures = [
      { text: "QR code personnalisé", included: true },
      { text: "Collecte d'avis illimitée", included: true },
      { text: "Redirection automatique Google (≥4★)", included: true },
      { text: "Tableau de bord & KPIs", included: true },
      { text: "Plan d'action basé sur vos avis", included: true },
      { text: "Alertes avis négatifs (<4 étoiles)", included: true },
      { text: "Support par email", included: true }
    ];
    
    // Add trial feature only if eligible
    if (trialEligibility?.eligible_for_trial) {
      return [{ text: "3 jours d'essai gratuit", included: true }, ...baseFeatures];
    }
    return baseFeatures;
  };

  const plans = [
    {
      id: "starter",
      name: "Starter",
      price: "49",
      description: "Tout ce qu'il faut pour gérer votre e-réputation",
      features: getFeatures(),
      popular: true
    }
  ];

  const isTrial = isTrialActive();
  const trialDaysLeft = getTrialDaysLeft();
  const isPaymentFailed = subscription?.status === "payment_failed";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="subscription-page">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-3">
          Votre abonnement
        </h1>
        <p className="text-slate-500">
          {trialEligibility?.eligible_for_trial 
            ? "3 jours d'essai gratuit dès la création du compte. Prélèvement automatique après l'essai."
            : "Abonnez-vous pour accéder à toutes les fonctionnalités. Prélèvement automatique mensuel."
          }
        </p>
      </div>

      {/* Payment Failed Alert */}
      {isPaymentFailed && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 max-w-4xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800 mb-1">Paiement échoué</h3>
              <p className="text-red-700 text-sm mb-3">
                Votre dernier paiement a échoué. Veuillez mettre à jour vos informations de paiement pour continuer à utiliser le service.
              </p>
              <Button
                onClick={handleUpdatePayment}
                disabled={loading === "update-payment"}
                className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="update-payment-button"
              >
                {loading === "update-payment" ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Mettre à jour le paiement
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Trial Days Counter - Show when in trial period */}
      {isTrial && trialDaysLeft > 0 && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 max-w-4xl mx-auto text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-7 h-7" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Période d'essai en cours</h3>
                <p className="text-white/90 text-sm">
                  Votre essai gratuit se termine dans {trialDaysLeft} jour{trialDaysLeft > 1 ? 's' : ''}.
                  {subscription?.trial_ends_at && (
                    <span className="block text-white/70 text-xs mt-1">
                      Fin le {new Date(subscription.trial_ends_at).toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{trialDaysLeft}</div>
              <div className="text-white/80 text-sm">jour{trialDaysLeft > 1 ? 's' : ''} restant{trialDaysLeft > 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>
      )}

      {/* Trial Ending Today */}
      {isTrial && trialDaysLeft === 0 && (
        <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl p-6 max-w-4xl mx-auto text-white">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Votre essai se termine aujourd'hui !</h3>
              <p className="text-white/90 text-sm">
                Votre carte sera débitée de 49€ à la fin de la journée. Annulez maintenant si vous ne souhaitez pas continuer.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {alerts.map((alert) => (
        <div 
          key={alert.id}
          className={`rounded-2xl p-4 max-w-4xl mx-auto flex items-center justify-between ${
            alert.type === "payment_failed" ? "bg-red-50 border border-red-100" :
            alert.type === "trial_ending" ? "bg-amber-50 border border-amber-100" :
            "bg-blue-50 border border-blue-100"
          }`}
        >
          <div className="flex items-center gap-3">
            {alert.type === "payment_failed" ? (
              <AlertTriangle className="w-5 h-5 text-red-600" />
            ) : alert.type === "trial_ending" ? (
              <Clock className="w-5 h-5 text-amber-600" />
            ) : (
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
            )}
            <span className={`text-sm ${
              alert.type === "payment_failed" ? "text-red-800" :
              alert.type === "trial_ending" ? "text-amber-800" :
              "text-blue-800"
            }`}>
              {alert.message}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dismissAlert(alert.id)}
            className="text-slate-400 hover:text-slate-600"
          >
            <XCircle className="w-4 h-4" />
          </Button>
        </div>
      ))}

      {/* Trial Banner - Only show if eligible for trial */}
      {!subscription && trialEligibility?.eligible_for_trial && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 text-white max-w-4xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Gift className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">3 jours d'essai gratuit</h3>
              <p className="text-white/90 text-sm">
                Testez toutes les fonctionnalités sans engagement. Annulez quand vous voulez, même pendant l'essai.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Resubscribe Banner - Show when user had a subscription before but cancelled */}
      {!subscription && trialEligibility && !trialEligibility.eligible_for_trial && (
        <div className="bg-gradient-to-r from-violet-500 to-purple-500 rounded-2xl p-6 text-white max-w-4xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Réactivez votre abonnement</h3>
              <p className="text-white/90 text-sm">
                Reprenez là où vous en étiez. Accédez immédiatement à toutes les fonctionnalités.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Status Banner */}
      {subscription && (
        <div className={`rounded-2xl p-4 max-w-lg mx-auto text-center ${
          isTrial ? "bg-amber-50 border-2 border-amber-200" : "bg-emerald-50 border-2 border-emerald-200"
        }`}>
          {isTrial ? (
            <div className="flex items-center justify-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-amber-800">
                Essai gratuit : {trialDaysLeft} jour{trialDaysLeft > 1 ? "s" : ""} restant{trialDaysLeft > 1 ? "s" : ""}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span className="font-medium text-emerald-800">
                Abonnement Starter actif
              </span>
            </div>
          )}
        </div>
      )}

      {/* Billing Period Toggle */}
      {(!subscription || subscription.status === "cancelled") && (
        <div className="flex justify-center mb-6">
          <div className="bg-slate-100 p-1 rounded-full inline-flex items-center">
            <button
              onClick={() => setBillingPeriod("monthly")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingPeriod === "monthly"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Mensuel
            </button>
            <button
              onClick={() => setBillingPeriod("annual")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all relative ${
                billingPeriod === "annual"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Annuel
              <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                -17%
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="flex justify-center max-w-lg mx-auto">
        {plans.map((plan) => (
          <div 
            key={plan.id}
            className="relative bg-white rounded-2xl p-8 border-2 border-violet-500 shadow-xl shadow-violet-500/10 w-full"
            data-testid={`plan-${plan.id}`}
          >
            {/* Popular Badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center px-4 py-1 bg-gradient-to-r from-violet-600 to-pink-500 text-white text-sm font-medium rounded-full">
                {billingPeriod === "annual" ? "2 mois offerts" : "Recommandé"}
              </span>
            </div>

            {/* Plan Header */}
            <div className="mb-6 mt-2">
              <h3 className="text-2xl font-bold text-slate-900">{plan.name}</h3>
              <p className="text-slate-500 mt-1">{plan.description}</p>
            </div>

            {/* Price */}
            <div className="mb-6">
              {billingPeriod === "annual" ? (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-slate-900">490€</span>
                    <span className="text-slate-500">/an TTC</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-slate-400 line-through text-lg">588€</span>
                    <span className="text-emerald-600 font-semibold text-sm bg-emerald-50 px-2 py-0.5 rounded-full">
                      Économisez 98€
                    </span>
                  </div>
                  <p className="text-slate-500 text-sm mt-1">
                    Soit 40,83€/mois · Engagement 12 mois
                  </p>
                </>
              ) : (
                <>
                  <span className="text-5xl font-bold text-slate-900">{plan.price}€</span>
                  <span className="text-slate-500">/mois TTC</span>
                  <p className="text-slate-500 text-sm mt-1">Sans engagement</p>
                </>
              )}
            </div>

            {/* Annual Bonus */}
            {billingPeriod === "annual" && (
              <div className="bg-gradient-to-r from-violet-50 to-pink-50 rounded-xl p-4 mb-6 border border-violet-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">2 mois offerts</p>
                    <p className="text-slate-500 text-xs">Payez 10 mois, profitez 12 mois</p>
                  </div>
                </div>
              </div>
            )}

            {/* Features */}
            <ul className="space-y-4 mb-8">
              {plan.features.map((feature, idx) => (
                <li 
                  key={idx} 
                  className="flex items-center gap-3 text-slate-700"
                >
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-500" />
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>

            {/* Action Button - Subscribe or Unsubscribe */}
            {subscription && (subscription.status === "active" || subscription.status === "trial") ? (
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={cancelling}
                className="w-full h-12 text-red-600 border-red-200 hover:bg-red-50 rounded-full"
                data-testid="unsubscribe-button"
              >
                {cancelling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Se désabonner
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleSubscribe}
                disabled={loading === "subscribe"}
                className="w-full h-12 rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90"
                data-testid="subscribe-button"
              >
                {loading === "subscribe" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : trialEligibility?.eligible_for_trial ? (
                  <>
                    <Gift className="w-4 h-4 mr-2" />
                    Essai gratuit 3 jours puis 49€/mois
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    S'abonner - 49€/mois
                  </>
                )}
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Test Card Info - Only for authorized emails */}
      {testModeInfo?.can_use_test_cards && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200 max-w-2xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <TestTube2 className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-amber-900 mb-2">Mode Démo - Carte de test</h3>
              <p className="text-amber-700 text-sm mb-3">
                Vous pouvez utiliser cette carte de test pour vos démonstrations :
              </p>
              <div className="bg-white rounded-xl p-4 border border-amber-200 font-mono text-sm space-y-1">
                <p><span className="text-slate-500">Numéro :</span> <strong className="text-slate-900">4242 4242 4242 4242</strong></p>
                <p><span className="text-slate-500">Date :</span> <strong className="text-slate-900">12/28</strong></p>
                <p><span className="text-slate-500">CVC :</span> <strong className="text-slate-900">123</strong></p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAQ */}
      <div className="text-center text-sm text-slate-500 max-w-2xl mx-auto space-y-2">
        <p className="font-medium text-slate-700">Sans engagement, annulable à tout moment</p>
        <p>
          {trialEligibility?.eligible_for_trial ? (
            "Paiement sécurisé par Stripe. Prélèvement automatique de 49€/mois après les 3 jours d'essai gratuit. Vous pouvez annuler à tout moment depuis cette page."
          ) : (
            "Paiement sécurisé par Stripe. Prélèvement automatique de 49€/mois. Vous pouvez annuler à tout moment depuis cette page."
          )}
        </p>
      </div>
    </div>
  );
};

export default Subscription;
